import { Ban, Car, Clock, Route, ShieldCheck, Wallet } from "lucide-react";
import Seo from "../components/Seo";
import DriverForm from "../components/forms/DriverForm";
import { IMAGES } from "../config/site";

const benefits = [
  { icon: Ban, text: "No monthly subscription" },
  { icon: Ban, text: "No obligation to accept jobs" },
  { icon: Clock, text: "No change to normal working patterns required" },
  { icon: Car, text: "Use your existing licensed Black Cab" },
  { icon: Route, text: "Earn money from otherwise empty journeys" },
  { icon: Wallet, text: "Accept suitable jobs only — you always decide" },
];

export default function Drivers() {
  return (
    <main data-testid="drivers-page">
      <Seo
        title="Essex Black Cab Drivers — Join Our Network"
        description="TfL Green Badge drivers living in Essex: turn your empty journeys between Essex and London into paid work. No subscription, no obligation — accept only bookings that suit your plans."
      />

      <section className="relative overflow-hidden border-b border-[#27272A]">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={IMAGES.driver.url} alt={IMAGES.driver.alt} className="h-full w-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/75 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-24">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">
            Essex Black Cab drivers
          </p>
          <h1 className="mt-4 max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            TURN EMPTY JOURNEYS INTO PAID WORK
          </h1>
          <p className="mt-6 max-w-xl leading-relaxed text-[#D4D4D8]">
            Are you a TfL-licensed London Green Badge Black Cab driver living in Essex? If
            you're already driving towards London or heading home to Essex, join our network
            and carry a passenger along the route you are travelling anyway.
          </p>
        </div>
      </section>

      <section className="section-pad mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">Why drivers join</h2>
            <ul className="mt-8 space-y-4">
              {benefits.map((b) => (
                <li key={b.text} className="flex items-start gap-3 text-[#D4D4D8]">
                  <b.icon className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" />
                  <span>{b.text}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm leading-relaxed text-[#71717A]">
              There is no guaranteed volume of bookings. Jobs are shared with our private,
              verified driver group, and you choose which ones to accept.
            </p>
          </div>
          <div className="border-[#27272A] lg:border-l lg:pl-12">
            <h2 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">How bookings reach you</h2>
            <ul className="mt-8 space-y-4 text-[#D4D4D8]">
              <li className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" />
                <span>Passengers request fixed-fare £100 journeys between Essex and London through this website.</span>
              </li>
              <li className="flex items-start gap-3">
                <Route className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" />
                <span>Suitable journeys are offered privately to our verified Essex driver group, with anonymised details.</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" />
                <span>You express interest only in jobs that fit your existing travel plans. There is never any obligation.</span>
              </li>
              <li className="flex items-start gap-3">
                <Car className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" />
                <span>The passenger pays you directly inside your cab — cash or your own card terminal.</span>
              </li>
            </ul>
            <p className="mt-6 border border-[#27272A] bg-[#111111] p-5 text-sm leading-relaxed text-[#A1A1AA]">
              Applications are reviewed manually — nothing is approved automatically. The full
              driver referral terms, including exactly how payment and referral fees work, are
              set out in the application section below so you can read them before you apply.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-[#27272A] bg-[#0B0B0B]">
        <div className="section-pad mx-auto max-w-3xl px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">Apply now</p>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight text-white sm:text-3xl">
            Join the Essex driver network
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-[#A1A1AA]">
            Applications are open to <strong className="text-white">TfL Green Badge (All London) Black Cab drivers only</strong>.
            Your details are used only to review your application and are never published. A
            clear photo or PDF of your current TfL taxi driver licence is required with your
            application — every applicant is verified manually before receiving any work.
          </p>

          <div
            data-testid="driver-terms-summary"
            className="mt-8 border border-[rgba(212,175,55,0.4)] bg-[rgba(212,175,55,0.06)] p-6"
          >
            <h3 className="text-lg font-semibold text-white">
              Driver referral terms — please read before applying
            </h3>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-[#D4D4D8]">
              <li>You accept a suitable referred £100 booking that fits your plans.</li>
              <li>You complete the journey.</li>
              <li>The passenger pays you the full £100 directly inside your taxi (cash or your card terminal).</li>
              <li>You subsequently pay Essex Black Cab a separate £20 referral fee for the completed referred journey.</li>
              <li>You retain £80 after the referral fee.</li>
            </ol>
            <p className="mt-4 text-xs leading-relaxed text-[#A1A1AA]">
              No referral fee is due when a journey is not completed. The £20 fee is paid
              separately by bank transfer within 24 hours of the completed journey. There is no
              monthly subscription and no obligation to accept any job. You must hold valid TfL
              driver and vehicle licensing and appropriate insurance. Read the full{" "}
              <a href="/driver-terms" className="text-[#D4AF37] underline-offset-4 hover:underline">
                Driver Referral Terms
              </a>{" "}
              — we obtain every driver's explicit written agreement before allocating jobs.
            </p>
          </div>

          <div className="mt-8 border border-[#27272A] bg-[#050505] p-6 sm:p-10">
            <DriverForm />
          </div>
        </div>
      </section>
    </main>
  );
}
